import React, { useState } from 'react';
import clsx from 'clsx';

import Button from '@common/Button';
import Container from '@common/Container';
import Section from '@common/Section';
import SmallTitle from '@common/SmallTitle';
import Img from '@common/Image';
import Row from '@common/Row';
import RichText from '@common/RichText';

import Video from './../Video';

import Apple from '@images/name-generator/apple.svg';
import Birds from '@images/name-generator/birds.svg';
import Cupcake from '@images/name-generator/cupcake.svg';
import Dog from '@images/name-generator/dog.svg';
import Flowers from '@images/name-generator/flowers.svg';
import Heart from '@images/name-generator/heart.svg';
import Star from '@images/name-generator/star.svg';
import Sun from '@images/name-generator/sun.svg';

import * as styles from '@styles/modules/resources.module.scss';

const NameGenerator = ({ data }) => {
  const {
    title,
    showTitle,
    text,
    listClass,
    imagePosition,
    imagePositionTop,
    imageWidth,
    imageMaxHeight,
    imageNegativeMargin,
    buttons,
    images,
    video,
    topMargin,
    bgColour,
    bgFullwidth
  } = data;
  const [daycareName, setName] = useState(null);
  const [daycareImage, setImage] = useState(null);
  const [showImg, setShowImg] = useState(true);
  const [buttonText, setButtonText] = useState(buttons[0]?.copy)

  const daycareLogos = [
    Apple,
    Birds,
    Cupcake,
    Dog,
    Flowers,
    Heart,
    Star,
    Sun
  ];

  const buttonTextList = [
    'That\'s a great one. Try again?',
    'Get that trademarked! Try again?',
    'Not sure about that one... try again?',
    'So cute! Satisfied?',
    'Take this one to the bank! Another?',
    'Established 2017. One more try?',
    'This one has a nice ring to it. Again?',
    'Hmm... another?',
  ];

  const firstWordList = [
    'Smiling',
    'Shining',
    'Rainbow',
    'Granny\'s',
    'Grandma\'s',
    'Mama\'s',
    'Daddy\'s',
    'Yellow',
    'Green',
    'Purple',
    'Pink',
    'Ladybug',
    'Wee',
    'Baby',
    'Little',
    'Beyond',
    'Positive',
    'Hope',
    'Academic',
    'Tiny',
    'Sunny',
    'Sunshine',
    'Peekaboo',
    'Children\'s',
    'Tender',
    'Kidz',
    'Kiddy',
    'Lovely',
    'Pretty',
    'Busy',
    'Creative',
    'Good',
    'Lullaby',
    'Learning',
    'Growing',
    'Imaginative',
    'Sweet',
    'Bright',
    'Happy',
  ];

  const secondWordList = [
    'Hands',
    'Toes',
    'Baby',
    'Babies',
    'Sun',
    'Stars',
    'Moon',
    'Express',
    'Genius',
    'Bear',
    'Learners',
    'Clover',
    'Daisy',
    'Care',
    'Wolf',
    'Miracles',
    'Learning',
    'Stones',
    'Sprouts',
    'Steps',
    'Friend',
    'Friends',
    'Folks',
    'Tree',
    'Apple',
    'Years',
    'Kidz',
    'Kids',
    'House',
    'Paradise',
    'Sandbox',
    'Blossoms',
    'Bee',
    'Kittens',
    'Seasons',
    'Flowers',
    'Tots',
    'Face',
    'Imagination',
    'Village',
    'Town',
    'City',
    'Feet',
  ];

  const thirdWordList = [
    'Center',
    'Daycare',
    'Preschool',
    'LLC',
    'Geniuses',
    'Program',
    'School',
    'CDC',
    'Childcare',
    'Montessori',
    'Friends',
    'House',
    'Inc.',
    'Child Care',
    'Program',
    'Education',
    'Home Care',
    'Dayhome',
    'Nursery',
    'Academy',
  ];

  const getRandomValue = (array, currentValue) => {
    // Added a filter to ensure the same image isn't shown twice
    const filteredArray = array.filter(value => value !== currentValue);
    const index = Math.floor(Math.random() * filteredArray.length)
    return filteredArray[index];
  }

  const generateName = () => {
    if (setShowImg) {
      setShowImg(false);
    }
    let newName = '';
    let newButtonText = '';
    let newLogo = '';
    do {
      const firstWord = getRandomValue(firstWordList);
      const secondWord = getRandomValue(secondWordList)
      const thirdWord = getRandomValue(thirdWordList);
      newLogo = getRandomValue(daycareLogos, daycareImage);
      newButtonText = getRandomValue(buttonTextList, buttonText);
      newName = `${firstWord} ${secondWord} ${thirdWord}`;
    } while (newName.length > 20);
    // Set the new values
    setName(newName);
    setButtonText(newButtonText);
    setImage(newLogo);
  }
  return (
    <Section style={{ marginTop: imageNegativeMargin }} className={clsx(topMargin, {
        [`bg-${bgColour}`]: bgColour,
        'py-5': bgColour && !bgNoPadding,
        'py-5 py-lg-0': bgColour && bgNoPadding,
        ['container rounded']: !bgFullwidth
      })}>
      <Container>
        {video &&
          <Video data={data}/>
        }
        <Row className={clsx(`d-flex flex-column align-items-center`, {
            'flex-md-row-reverse': imagePosition === 'Left',
            'flex-md-row': imagePosition === 'Right',
            'flex-column-reverse': imagePositionTop,
            'justify-content-center': !images && textAlignment === 'Center'
          })}>
          {text &&
            <div className={clsx('col', {
                'text-center col-lg-10': images && imagePosition === 'Center',
                'text-end': !images && textAlignment === 'Right'
              })}>
              {(showTitle && title) &&
                <SmallTitle>{title}</SmallTitle>
              }
              {text &&
                <RichText
                  json={text}
                  h1='mb-3'
                  h2='mb-3'
                  p='mb-3'
                  li='py-2'
                  a='fs-16'
                  ul={listClass}
                />
              }
            </div>
          }
          <div className={clsx(`col-12 text-center mx-auto`, styles.nameGen, imageWidth)}>
            {(images && showImg) &&
              <div className='col mb-4'>
                {images.map(image => (
                  <Img
                    key={image.id}
                    loading='eager'
                    image={image.gatsbyImageData}
                    file={image.file}
                    alt={image.description}
                    style={{ maxHeight: imageMaxHeight }}
                    imgStyle={{ objectFit: imageMaxHeight ? 'contain' : ''}}
                  />  
                ))}
              </div>
            }
            {daycareImage &&
              <img src={daycareImage} alt={''} aria-hidden={`true`} className={clsx('mb-4', styles.nameGenImg)} />
            }
            {daycareName && <p className={clsx(`mb-3`, styles.name)}>{daycareName}</p>}
            {buttons[0] && 
              <Button
                key={buttons[0].id}
                to={buttons[0].link}
                variant={buttons[0].variant}
                size={buttons[0].size}
                addClass={clsx('scale-1', buttons[0].class)}
                onClick={generateName}
                copy={buttonText}
              />
            }
          </div>
        </Row>
      </Container>
    </Section>
  )
}

export default NameGenerator
